import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useNavigate } from 'react-router-dom';
import { StatusPedido } from '../../types/pedido';
import { useSalesReport } from '../../hooks/useSalesReport';
import { SalesReportAggregatedItem, SalesReportDetailItem, SalesReportSummary } from '../../types/reports';
import authService from '../../services/authService';

interface SalesFiltersState {
  dataInicio: string;
  dataFim: string;
  status: StatusPedido[];
  canais: string[];
  formasPagamento: string[];
  cliente: string;
  telefone: string;
  cnpj: string;
}

const formatDateInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const STATUS_OPTIONS: { value: StatusPedido; label: string; description: string }[] = [
  { value: StatusPedido.RECEBIDO, label: 'Recebido', description: 'Pedidos recebidos e aguardando preparo' },
  { value: StatusPedido.EM_PREPARO, label: 'Em preparo', description: 'Pedidos em preparação' },
  { value: StatusPedido.PRONTO, label: 'Pronto', description: 'Pedidos prontos para entrega' },
  { value: StatusPedido.EM_ROTA, label: 'Em rota', description: 'Pedidos em rota de entrega' },
  { value: StatusPedido.ENTREGUE, label: 'Entregue', description: 'Pedidos entregues ao cliente' },
  { value: StatusPedido.CANCELADO, label: 'Cancelado', description: 'Pedidos cancelados' },
];

const CANAL_OPTIONS: { value: string; label: string }[] = [
  { value: 'APP', label: 'App / Delivery' },
  { value: 'WHATSAPP', label: 'WhatsApp / Balcão' },
  { value: 'MAIS_DELIVERY', label: 'Mais Delivery' },
];

const PAYMENT_METHOD_OPTIONS: { value: string; label: string }[] = [
  { value: 'PIX', label: 'PIX' },
  { value: 'CREDIT_CARD', label: 'Cartão de crédito' },
  { value: 'DEBIT_CARD', label: 'Cartão de débito' },
  { value: 'OTHER', label: 'Outros' },
];

const SalesReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<SalesFiltersState>(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);

    return {
      dataInicio: formatDateInput(firstDay),
      dataFim: formatDateInput(now),
      status: [StatusPedido.ENTREGUE],
      canais: CANAL_OPTIONS.map((option) => option.value),
      formasPagamento: [],
      cliente: '',
      telefone: '',
      cnpj: '',
    };
  });

  const user = useMemo(() => authService.getUser(), []);

  const queryParams = useMemo(() => {
    const params: {
      dataInicio?: string;
      dataFim?: string;
      status?: string[];
      canais?: string[];
      formasPagamento?: string[];
      cliente?: string;
      telefone?: string;
      cnpj?: string;
    } = {};

    if (filters.dataInicio) {
      params.dataInicio = new Date(`${filters.dataInicio}T00:00:00`).toISOString();
    }
    if (filters.dataFim) {
      params.dataFim = new Date(`${filters.dataFim}T23:59:59`).toISOString();
    }
    if (filters.status.length > 0) {
      params.status = filters.status;
    }
    if (filters.canais.length > 0 && filters.canais.length !== CANAL_OPTIONS.length) {
      params.canais = filters.canais;
    }
    if (filters.formasPagamento.length > 0) {
      params.formasPagamento = filters.formasPagamento;
    }
    if (filters.cliente.trim()) {
      params.cliente = filters.cliente.trim();
    }
    if (filters.telefone.trim()) {
      params.telefone = filters.telefone.replace(/\D/g, '');
    }
    const cnpjInput = filters.cnpj.trim();
    const effectiveCnpj = cnpjInput || user?.cnpj || '';
    if (effectiveCnpj) {
      params.cnpj = effectiveCnpj.replace(/\D/g, '');
    }

    return params;
  }, [filters, user?.cnpj]);

  const { data, isLoading, isError, isFetching, refetch } = useSalesReport(queryParams, filters.status.length > 0);

  const resumo: SalesReportSummary | undefined = data?.resumo;
  const agregados: SalesReportAggregatedItem[] = data?.agregados ?? [];
  const detalhes: SalesReportDetailItem[] = data?.detalhes ?? [];

  const totalizadores = useMemo(() => {
    if (!resumo) {
      return {
        totalPedidos: 0,
        valorBruto: 0,
        valorLiquido: 0,
        ticketMedio: 0,
      };
    }

    return {
      totalPedidos: resumo.totalPedidos,
      valorBruto: resumo.valorBruto,
      valorLiquido: resumo.valorLiquido,
      ticketMedio: resumo.ticketMedio,
    };
  }, [resumo]);

  const handleStatusChange = (_event: React.MouseEvent<HTMLElement>, value: StatusPedido[]) => {
    if (value.length === 0) {
      return;
    }
    setFilters((prev) => ({
      ...prev,
      status: value,
    }));
  };

  const handleCanalToggle = (_event: React.MouseEvent<HTMLElement>, value: string[]) => {
    if (value.length === 0) {
      return;
    }
    setFilters((prev) => ({
      ...prev,
      canais: value,
    }));
  };

  const handleFormasPagamentoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const options = event.target.value as unknown as string[];
    setFilters((prev) => ({
      ...prev,
      formasPagamento: options,
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClearFilters = () => {
    refetch();
  };

  const handleNavigateToPrint = () => {
    const params = new URLSearchParams();
    if (filters.dataInicio) params.set('dataInicio', filters.dataInicio);
    if (filters.dataFim) params.set('dataFim', filters.dataFim);
    filters.status.forEach((status) => params.append('status', status));
    if (filters.canais.length > 0) filters.canais.forEach((canal) => params.append('canais', canal));
    if (filters.formasPagamento.length > 0)
      filters.formasPagamento.forEach((forma) => params.append('formasPagamento', forma));
    if (filters.cliente.trim()) params.set('cliente', filters.cliente.trim());
    if (filters.telefone.trim()) params.set('telefone', filters.telefone.replace(/\D/g, ''));
    const cnpjInput = filters.cnpj.trim();
    const effectiveCnpj = cnpjInput || user?.cnpj || '';
    if (effectiveCnpj) params.set('cnpj', effectiveCnpj.replace(/\D/g, ''));

    navigate(`/reports/sales/print?${params.toString()}`);
  };

  const renderResumoCards = () => (
    <Box
      className="screen-only"
      display="grid"
      gap={2}
      gridTemplateColumns={{ xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }}
    >
      <Card>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Total de pedidos
          </Typography>
          <Typography variant="h4">{totalizadores.totalPedidos}</Typography>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Valor bruto
          </Typography>
          <Typography variant="h4">{formatCurrency(totalizadores.valorBruto)}</Typography>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Valor líquido
          </Typography>
          <Typography variant="h4">{formatCurrency(totalizadores.valorLiquido)}</Typography>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Ticket médio
          </Typography>
          <Typography variant="h4">{formatCurrency(totalizadores.ticketMedio)}</Typography>
        </CardContent>
      </Card>
    </Box>
  );

  const renderResumoDistribuicoes = () => {
    if (!resumo) {
      return null;
    }

    const statusEntries = Object.entries(resumo.pedidosPorStatus || {});
    const canalEntries = Object.entries(resumo.pedidosPorCanal || {});

    return (
      <Box
        className="screen-only"
        display="grid"
        gap={2}
        gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
      >
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Pedidos por status
            </Typography>
            {statusEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum status encontrado.
              </Typography>
            ) : (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {statusEntries.map(([status, count]) => (
                  <Chip key={status} label={`${status}: ${count}`} />
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Pedidos por canal
            </Typography>
            {canalEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum canal encontrado.
              </Typography>
            ) : (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {canalEntries.map(([canal, count]) => (
                  <Chip key={canal} label={`${canal}: ${count}`} />
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderAggregatedTable = () => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6">Resumo diário</Typography>
            <Typography variant="body2" color="text.secondary">
              Valores consolidados por dia dentro do período filtrado.
            </Typography>
          </Box>
          {isFetching && <CircularProgress size={24} />}
        </Box>
        {agregados.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum registro encontrado para o período selecionado.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell align="right">Pedidos</TableCell>
                <TableCell align="right">Valor dos itens</TableCell>
                <TableCell align="right">Taxas</TableCell>
                <TableCell align="right">Descontos</TableCell>
                <TableCell align="right">Valor total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agregados.map((item) => (
                <TableRow key={item.referencia}>
                  <TableCell>{item.referencia}</TableCell>
                  <TableCell align="right">{item.quantidadePedidos}</TableCell>
                  <TableCell align="right">{formatCurrency(item.valorItens)}</TableCell>
                  <TableCell align="right">{formatCurrency(item.valorTaxas)}</TableCell>
                  <TableCell align="right">{formatCurrency(item.descontoAplicado)}</TableCell>
                  <TableCell align="right">{formatCurrency(item.valorTotal)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );

  const renderDetailsTable = () => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Pedidos detalhados</Typography>
          {isFetching && <CircularProgress size={24} />}
        </Box>
        {detalhes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum pedido detalhado para o período e filtros informados.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>Canal</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Pagamento</TableCell>
                <TableCell align="right">Itens</TableCell>
                <TableCell align="right">Taxa</TableCell>
                <TableCell align="right">Desconto</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detalhes.map((pedido) => (
                <TableRow key={pedido.id}>
                  <TableCell>{formatDateTime(pedido.dataPedido)}</TableCell>
                  <TableCell>{pedido.cliente}</TableCell>
                  <TableCell>{pedido.telefone || '-'}</TableCell>
                  <TableCell>{pedido.canal}</TableCell>
                  <TableCell>{pedido.status}</TableCell>
                  <TableCell>{pedido.formaPagamento || '-'}</TableCell>
                  <TableCell align="right">{formatCurrency(pedido.valorItens)}</TableCell>
                  <TableCell align="right">{formatCurrency(pedido.valorTaxa)}</TableCell>
                  <TableCell align="right">{formatCurrency(pedido.desconto)}</TableCell>
                  <TableCell align="right">{formatCurrency(pedido.valorTotal)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        flexWrap="wrap"
        gap={2}
        className="screen-only"
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Relatório de vendas
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Acompanhe o desempenho comercial com filtros avançados, visão consolidada por dia e detalhes
            de cada pedido.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handleNavigateToPrint}
          disabled={isLoading || isFetching}
          sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
        >
          Imprimir
        </Button>
      </Box>

      <Card className="screen-only">
        <CardContent>
          <Box
            display="grid"
            gap={2}
            gridTemplateColumns={{ xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }}
          >
            <Box>
              <TextField
                label="Data inicial"
                name="dataInicio"
                type="date"
                value={filters.dataInicio}
                onChange={handleInputChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <TextField
                label="Data final"
                name="dataFim"
                type="date"
                value={filters.dataFim}
                onChange={handleInputChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <TextField
                label="Cliente"
                name="cliente"
                value={filters.cliente}
                onChange={handleInputChange}
                fullWidth
                size="small"
                placeholder="Nome ou parte do nome"
              />
            </Box>
            <Box>
              <TextField
                label="Telefone"
                name="telefone"
                value={filters.telefone}
                onChange={handleInputChange}
                fullWidth
                size="small"
                placeholder="(00) 00000-0000"
              />
            </Box>
            <Box>
              <TextField
                label="CNPJ"
                name="cnpj"
                value={filters.cnpj}
                onChange={handleInputChange}
                fullWidth
                size="small"
                placeholder="Somente números"
                helperText="Disponível apenas para usuários Desenvolvedores"
              />
            </Box>
            <Box>
              <TextField
                label="Formas de pagamento"
                select
                SelectProps={{ multiple: true, native: true }}
                value={filters.formasPagamento}
                onChange={handleFormasPagamentoChange}
                fullWidth
                size="small"
                helperText="Selecione uma ou mais formas. Deixe vazio para todas."
              >
                {PAYMENT_METHOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TextField>
            </Box>
            <Box gridColumn={{ xs: '1 / -1', md: 'span 4' }}>
              <Typography variant="subtitle1">Canais de venda</Typography>
              <Typography variant="body2" color="text.secondary">
                Marque os canais que deseja considerar. Todos os canais são incluídos por padrão.
              </Typography>
              <ToggleButtonGroup
                value={filters.canais}
                onChange={handleCanalToggle}
                color="primary"
                size="small"
                sx={{ mt: 1, flexWrap: 'wrap' }}
              >
                {CANAL_OPTIONS.map((option) => (
                  <ToggleButton key={option.value} value={option.value} size="small" sx={{ textTransform: 'none', m: 0.5 }}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                {filters.canais.map((canal) => {
                  const option = CANAL_OPTIONS.find((item) => item.value === canal);
                  return <Chip key={canal} label={option?.label ?? canal} />;
                })}
              </Box>
            </Box>
            <Box gridColumn={{ xs: '1 / -1', md: 'span 4' }}>
              <Typography variant="subtitle1">Status dos pedidos</Typography>
              <Typography variant="body2" color="text.secondary">
                Selecione os status considerados no relatório. Por padrão exibimos apenas pedidos entregues.
              </Typography>
              <ToggleButtonGroup
                value={filters.status}
                onChange={handleStatusChange}
                color="primary"
                size="small"
                sx={{ mt: 1, flexWrap: 'wrap' }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <ToggleButton key={option.value} value={option.value} size="small" sx={{ textTransform: 'none', m: 0.5 }}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                {filters.status.map((status) => {
                  const option = STATUS_OPTIONS.find((item) => item.value === status);
                  return (
                    <Chip
                      key={status}
                      label={option?.label ?? status}
                      color={status === StatusPedido.ENTREGUE ? 'success' : status === StatusPedido.CANCELADO ? 'error' : 'default'}
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="outlined" onClick={handleClearFilters} disabled={isLoading}>
              Atualizar
            </Button>
          </Box>
        </CardContent>
      </Card>

      {isError && (
        <Alert severity="error">
          Ocorreu um erro ao carregar o relatório de vendas. Tente ajustar os filtros ou recarregue a página.
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <>
          {renderResumoCards()}
          {renderResumoDistribuicoes()}
          {renderAggregatedTable()}
          {renderDetailsTable()}
        </>
      )}
    </Box>
  );
};

export default SalesReportPage;
